import React, { useEffect, useMemo, useRef, useState } from "react";
import SpecialtyCarousel from "./SpecialtyCarousel";
import SPYellowLogo from "../SVGs/SPYellowLogo";
import useMediaQueries from "@hooks/useMediaQueries";
import ArrowRight from "../SVGs/ArrowRight";
import { CtaButton } from "../Cta/cta-button";

export type PageModellink = {
  value: {
    data: {
      url: string;
    };
  };
};

export type specialtyListType = {
  title: string;
  openInNewTab?: boolean;
  ctaLink: PageModellink;
  ctaUrl?: string;
  mobileCtaCopy: string;
  image: string;
  mobileImage?: string;
  imageAltText: string;
  subCopy: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  topImageSlot?: any;
  bottomImageSlot?: any;
};

export type TextCarouselType = {
  eyebrowText?: string;
  ctaCopy: string;
  openInNewTab?: boolean;
  ctaLink: PageModellink;
  ctaUrl?: string;
  specialtyList: specialtyListType[];
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  builderBlock?: any;
  BuilderBlocksComponent?: any;
};

const TextCarousel: React.FC<TextCarouselType> = ({
  eyebrowText,
  ctaCopy,
  ctaLink,
  specialtyList,
  ctaUrl,
  openInNewTab,
  onClick,
  BuilderBlocksComponent,
  builderBlock,
}) => {
  const [indexItemActive, setIndexItemActive] = useState<number>(0);
  const [offsetTop, setOffsetTop] = useState<number[]>([]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const intervalRef = useRef<number | NodeJS.Timeout | null>(null);
  const { isDesktop, isMobile } = useMediaQueries();
  const { topImageSlot, bottomImageSlot } = builderBlock?.component?.options
    ?.specialtyList[indexItemActive] ?? {
    topImageSlot: "",
    bottomImageSlot: "",
  };
  const topImageSlotDataPath = `component.options.specialtyList.${indexItemActive}.topImageSlot`;
  const bottomImageSlotDataPath = `component.options.specialtyList.${indexItemActive}.bottomImageSlot`;

  const specialtyItem = useMemo(() => {
    return specialtyList?.[indexItemActive];
  }, [specialtyList, indexItemActive]);

  const yellowLogoPosition = useMemo(() => {
    if (indexItemActive === 0) return 40;

    return offsetTop[indexItemActive] - 15;
  }, [indexItemActive]);

  const clean = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
  };

  const executeAnimation = () => {
    let i = 1;

    const nodeInterval: NodeJS.Timeout = setInterval(() => {
      i++;
      setIndexItemActive(prev =>
        prev === specialtyList?.length - 1 ? 0 : prev + 1
      );
    }, 6000);

    intervalRef.current = nodeInterval;
  };

  useEffect(() => {
    const newOffsetTop = itemRefs.current.map(ref => ref?.offsetTop || 0);
    setOffsetTop(newOffsetTop);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    executeAnimation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [specialtyList]);

  return (
    <div className="text--carousel redesign-component">
      {!isDesktop && (
        <SpecialtyCarousel
          specialtyList={specialtyList}
          eyebrowText={eyebrowText}
          indexItemActive={indexItemActive}
          setIndexItemActive={setIndexItemActive}
          clean={clean}
          executeAnimation={executeAnimation}
          openInNewTab={openInNewTab}
          onClick={onClick}
        />
      )}
      <div className={`text--carousel__options force`}>
        <div className="text--carousel__eyebrow">
          <span className="text--carousel__eyebrow-text">{eyebrowText}</span>
        </div>
        <SPYellowLogo yellowLogoPosition={yellowLogoPosition} />
        {specialtyList?.length &&
          specialtyList?.map((item, key) => {
            const isActive = indexItemActive === key;
            const hasCtaLink = item.ctaLink;

            return (
              <div
                className={`text--carousel__options-item ${
                  isActive && "isActive"
                } `}
                key={key}
                ref={el => (itemRefs.current[key] = el)}
              >
                <a
                  href={
                    hasCtaLink ? item?.ctaLink?.value?.data?.url : item?.ctaUrl
                  }
                  className="text--carousel--marketing-link no-underline relative inline-block h-full"
                  target={!item?.openInNewTab ? "_self" : "_blank"}
                  onClick={onClick}
                  rel="noreferrer"
                >
                  <h3
                    className={`text--carousel__options-title inline ${
                      isActive && "isActive"
                    }`}
                    onMouseEnter={() => {
                      clean();
                      setIndexItemActive(key);
                    }}
                    onMouseLeave={executeAnimation}
                  >
                    {item?.title}
                  </h3>
                  {isActive && (
                    <span className="text--carousel__options-icon absolute bottom-[10px]">
                      <ArrowRight />
                    </span>
                  )}
                </a>
              </div>
            );
          })}
        <div className="mobile-cta-wrapper">
          {ctaCopy && (
            <CtaButton
              copy={ctaCopy}
              copySize="bodyRegular"
              isDisabled={false}
              link={ctaLink?.value?.data?.url}
              linkUrl={ctaUrl || null}
              variant="secondaryForest"
              openInNewTab={openInNewTab}
              onClick={onClick}
            />
          )}
        </div>
      </div>
      <div className="text--carousel__copy">
        <div className="text--carousel__image--wrapper relative">
          <BuilderBlocksComponent
            dataPath={topImageSlotDataPath}
            parentElementId={builderBlock?.id}
            className="absolute top-8 md:top-0 left-0 w-full"
            blocks={topImageSlot}
          />
          {isMobile ? (
            <img
              src={specialtyItem?.mobileImage}
              alt={specialtyItem?.imageAltText}
              className="text--carousel__copy-image"
            />
          ) : (
            <img
              src={specialtyItem?.image}
              alt={specialtyItem?.imageAltText}
              className="text--carousel__copy-image"
            />
          )}
          <BuilderBlocksComponent
            dataPath={bottomImageSlotDataPath}
            parentElementId={builderBlock?.id}
            className="absolute bottom-8 md:bottom-0 left-0 w-full"
            blocks={bottomImageSlot}
          />
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: specialtyItem?.subCopy || "" }}
          className="text--carousel__copy-text"
        />
        {ctaCopy && (
          <CtaButton
            copy={ctaCopy}
            copySize="bodyRegular"
            isDisabled={false}
            link={ctaLink?.value?.data?.url}
            linkUrl={ctaUrl || null}
            variant="secondaryForest"
            className="cta--button-wrapper cta--button--tablet"
            openInNewTab={openInNewTab}
            onClick={onClick}
          />
        )}
      </div>
    </div>
  );
};

export default TextCarousel;
